import { AlertCircle, Eye, EyeOff, Lock, Mail } from "lucide-react";
import type React from "react";
import { useId, useState } from "react";
import { Button, ButtonVariant } from "../../../../components/common/Button";
import { cn } from "../../../../lib/utils";
import {
	hasLoginFormErrors,
	type LoginFormFieldErrors,
	type LoginFormValues,
	validateLoginForm,
} from "../../loginForm.utils";
import { LoginTextField } from "../LoginTextField";
import { MicrosoftIcon } from "../MicrosoftIcon";
import type { LoginFormProps } from "./types";

const LoginForm = ({
	onCredentialsLogin,
	onSsoLogin,
	authError,
	isCredentialsSubmitting,
	isSsoRedirecting,
}: LoginFormProps) => {
	const emailId = useId();
	const passwordId = useId();
	const emailErrorId = useId();
	const passwordErrorId = useId();

	const [values, setValues] = useState<LoginFormValues>({
		email: "",
		password: "",
	});
	const [fieldErrors, setFieldErrors] = useState<LoginFormFieldErrors>({});
	const [touched, setTouched] = useState<
		Partial<Record<keyof LoginFormValues, boolean>>
	>({});
	const [showPassword, setShowPassword] = useState(false);

	const isBusy = isCredentialsSubmitting || isSsoRedirecting;

	const validateField = (
		field: keyof LoginFormValues,
		nextValues: LoginFormValues,
	) => {
		const nextErrors = validateLoginForm(nextValues);
		setFieldErrors((current) => ({
			...current,
			[field]: nextErrors[field],
		}));
	};

	const handleChange =
		(field: keyof LoginFormValues) =>
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const nextValues = { ...values, [field]: event.target.value };
			setValues(nextValues);
			if (touched[field]) {
				validateField(field, nextValues);
			}
		};

	const handleBlur = (field: keyof LoginFormValues) => () => {
		setTouched((current) => ({ ...current, [field]: true }));
		validateField(field, values);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const nextErrors = validateLoginForm(values);
		setFieldErrors(nextErrors);
		setTouched({ email: true, password: true });
		if (hasLoginFormErrors(nextErrors)) return;

		await onCredentialsLogin({
			email: values.email.trim(),
			password: values.password,
		});
	};

	return (
		<div className="login-fade-up w-full max-w-[420px]">
			<div className="mb-8 lg:mb-10">
				<p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-muted">
					Welcome back
				</p>
				<h2 className="mt-3 text-3xl font-bold tracking-tight text-primary">
					Sign in to your account
				</h2>
				<p className="mt-2 text-sm leading-relaxed text-body">
					Use your admin credentials or continue with your organization&apos;s
					single sign-on.
				</p>
			</div>

			{authError ? (
				<div
					className="login-shake mb-6 flex items-start gap-3 rounded-[var(--radius-input)] border border-red-200/80 bg-red-50 px-4 py-3.5 text-sm text-red-800 shadow-sm"
					role="alert"
					aria-live="polite"
				>
					<AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
					<div>
						<p className="font-medium">Unable to sign in</p>
						<p className="mt-0.5 text-red-700/90">{authError}</p>
					</div>
				</div>
			) : null}

			<form className="space-y-5" onSubmit={handleSubmit} noValidate>
				<LoginTextField
					id={emailId}
					label="Work email"
					name="email"
					type="email"
					icon={Mail}
					value={values.email}
					onChange={handleChange("email")}
					onBlur={handleBlur("email")}
					placeholder="you@kerzner.com"
					autoComplete="email"
					inputMode="email"
					disabled={isBusy}
					error={touched.email ? fieldErrors.email : undefined}
					errorId={emailErrorId}
				/>

				<LoginTextField
					id={passwordId}
					label="Password"
					name="password"
					type={showPassword ? "text" : "password"}
					icon={Lock}
					value={values.password}
					onChange={handleChange("password")}
					onBlur={handleBlur("password")}
					placeholder="Enter your password"
					autoComplete="current-password"
					disabled={isBusy}
					error={touched.password ? fieldErrors.password : undefined}
					errorId={passwordErrorId}
					trailing={
						<button
							type="button"
							onClick={() => setShowPassword((current) => !current)}
							disabled={isBusy}
							className={cn(
								"flex h-9 w-9 items-center justify-center rounded-lg text-icon transition-colors",
								"hover:bg-neutral-100 hover:text-primary",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20",
							)}
							aria-label={showPassword ? "Hide password" : "Show password"}
						>
							{showPassword ? (
								<EyeOff className="h-4 w-4" />
							) : (
								<Eye className="h-4 w-4" />
							)}
						</button>
					}
				/>

				<Button
					type="submit"
					variant={ButtonVariant.CONTAINED}
					fullWidth
					size="large"
					disabled={isBusy}
					className="!mt-7 !h-12 !rounded-[var(--radius-input)] !bg-accent !text-[15px] !font-semibold shadow-md shadow-accent/20 hover:!bg-[#8f1212] focus-visible:!ring-accent/30"
				>
					Sign in
				</Button>
			</form>

			<div className="my-8 flex items-center gap-4">
				<div className="h-px flex-1 bg-border" aria-hidden />
				<span className="text-xs font-medium uppercase tracking-[0.16em] text-primary-muted">
					or
				</span>
				<div className="h-px flex-1 bg-border" aria-hidden />
			</div>

			<button
				type="button"
				onClick={onSsoLogin}
				disabled={isBusy}
				className={cn(
					"group flex h-12 w-full items-center justify-center gap-3 rounded-[var(--radius-input)] border border-border bg-white px-4 text-sm font-semibold text-primary shadow-sm transition-all duration-200",
					"hover:border-neutral-300 hover:bg-bg-input hover:shadow-md",
					"focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent/10",
					"disabled:cursor-not-allowed disabled:opacity-60",
				)}
			>
				{isSsoRedirecting ? (
					<span className="text-primary-muted">Redirecting to Microsoft…</span>
				) : (
					<>
						<MicrosoftIcon className="h-[18px] w-[18px] transition-transform duration-200 group-hover:scale-105" />
						<span>Continue with Microsoft</span>
					</>
				)}
			</button>

			<p className="mt-8 text-center text-xs leading-relaxed text-primary-muted">
				By signing in, you agree to internal access policies.
				<br />
				Need help? Contact your system administrator.
			</p>
		</div>
	);
};

export { LoginForm };
